import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import { Info } from "lucide-react";

import { UIActionModal, UICallout, UITextarea } from "../../../../ui";

const isSameId = (first, second) =>
  first !== undefined &&
  first !== null &&
  second !== undefined &&
  second !== null &&
  String(first) === String(second);

const getAssignedZone = (applicant, zoneOptions = []) =>
  applicant?.zone ||
  zoneOptions.find((option) => isSameId(option.supervisorId, applicant?.userId))
    ?.name ||
  "";

const RemoveRequestModal = ({
  open,
  applicants,
  zoneOptions,
  copy,
  onClose,
  onRemove,
}) => {
  const [reason, setReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);
  const supervisors = useMemo(
    () => applicants.filter((applicant) => applicant.isSupervisor),
    [applicants],
  );
  const isBulk = applicants.length > 1;
  const primaryApplicant = applicants[0];
  const isSingleSupervisor = !isBulk && Boolean(primaryApplicant?.isSupervisor);
  const assignedZone = getAssignedZone(primaryApplicant, zoneOptions);
  const trimmedReason = reason.trim();
  const showReasonError = reasonTouched && !trimmedReason;
  const question = isBulk
    ? copy.bulkQuestion.replace("{count}", applicants.length)
    : copy.singleQuestion;
  const singleSupervisorDescription = (
    assignedZone
      ? copy.singleSupervisorDescription
      : copy.singleUnassignedSupervisorDescription
  ).replace("{zone}", assignedZone);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setReasonTouched(false);
  }, [open]);

  const handleRemove = () => {
    setReasonTouched(true);
    if (!trimmedReason) return;

    onRemove({ reason: trimmedReason });
  };

  return (
    <UIActionModal
      open={open}
      onClose={onClose}
      title={copy.title}
      variant="reject"
      maxWidth="sm"
      PaperProps={{ className: "event-applicants-remove-modal" }}
      actions={
        <UIActionModal.Actions
          cancelLabel={copy.cancel}
          confirmLabel={copy.remove}
          confirmVariant="reject"
          confirmDisabled={!trimmedReason}
          onCancel={onClose}
          onConfirm={handleRemove}
        />
      }
    >
      <UIActionModal.Divider />
      <Typography className="event-applicants-remove-modal__question">
        {question}
      </Typography>

      {isSingleSupervisor && (
        <UICallout
          variant="error"
          className="event-applicants-remove-modal__warning"
          icon={<Info size={20} strokeWidth={2} className="ui-callout__icon" />}
          title={copy.singleSupervisorTitle}
          description={singleSupervisorDescription}
          hasBorder={false}
        />
      )}

      {isBulk && supervisors.length > 0 && (
        <UICallout
          variant="error"
          className="event-applicants-remove-modal__warning"
          icon={<Info size={20} strokeWidth={2} className="ui-callout__icon" />}
          title={copy.bulkSupervisorTitle.replace(
            "{count}",
            supervisors.length,
          )}
          description={copy.bulkSupervisorDescription}
          hasBorder={false}
        />
      )}

      <UITextarea
        label={copy.reasonLabel}
        name="removalReason"
        value={reason}
        placeholder={copy.placeholder}
        onValueChange={(value) => {
          setReason(value);
          if (reasonTouched && value.trim()) {
            setReasonTouched(false);
          }
        }}
        onBlur={() => setReasonTouched(true)}
        required
        error={showReasonError}
        helperText={showReasonError ? copy.reasonRequired : null}
        minRows={3}
        classNames={{ root: "event-applicants-remove-modal__field" }}
      />
    </UIActionModal>
  );
};

RemoveRequestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  applicants: PropTypes.arrayOf(PropTypes.object),
  zoneOptions: PropTypes.arrayOf(PropTypes.object),
  copy: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

RemoveRequestModal.defaultProps = {
  applicants: [],
  zoneOptions: [],
};

export default RemoveRequestModal;
